import { t } from "@lingui/macro";
import { TOKEN_COSTS } from "@stylingcv/config";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  centralizedTokenService,
  type TokenBalance,
  type TokenConsumptionResult,
  TOKEN_BALANCE_EVENT,
} from "@/client/services/token/centralized-token.service";
import { useAuthStore } from "@/client/stores/auth";
import { useTokenStore } from "@/client/stores/token";

export type UseCentralizedTokensReturn = {
  tokenBalance: number;
  tokenInfo: TokenBalance | null;
  isLoading: boolean;
  error: string | null;
  refreshTokens: () => Promise<void>;
  consumeTokens: (
    feature: keyof typeof TOKEN_COSTS,
    description?: string,
  ) => Promise<TokenConsumptionResult>;
  canAfford: (feature: keyof typeof TOKEN_COSTS) => Promise<boolean>;
  getTokenCost: (feature: keyof typeof TOKEN_COSTS) => number;
  formatTokens: (amount: number) => string;
};

export const useCentralizedTokens = (): UseCentralizedTokensReturn => {
  // Use global store as the source of truth for token balance and loading state
  const globalTokenBalance = useTokenStore((state) => state.tokenBalance);
  const globalIsLoading = useTokenStore((state) => state.isLoading);
  const fetchTokenBalance = useTokenStore((state) => state.fetchTokenBalance);
  
  const [tokenInfo, setTokenInfo] = useState<TokenBalance | null>(null);
  const [localIsLoading, setLocalIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isLoggedIn = useAuthStore((state) => !!state.user);
  const mountedRef = useRef(true);
  const fetchInProgressRef = useRef(false); // Prevent duplicate fetches

  // Use global store balance as primary, fall back to local tokenInfo
  // null or undefined should fall back to 0 (not loaded yet)
  const tokenBalance = globalTokenBalance ?? tokenInfo?.balance ?? 0;
  
  // Loading if either global store is loading or local operation is in progress
  const isLoading = globalIsLoading || localIsLoading;

  const refreshTokens = useCallback(async () => {
    if (!mountedRef.current || fetchInProgressRef.current) return;

    fetchInProgressRef.current = true;
    setLocalIsLoading(true);
    setError(null);

    try {
      const balance = await centralizedTokenService.getTokenBalance(isLoggedIn, true);
      if (mountedRef.current) {
        setTokenInfo(balance);
      }
    } catch (error_) {
      const errorMessage = error_ instanceof Error ? error_.message : "Failed to fetch tokens";
      if (mountedRef.current) {
        setError(errorMessage);
      }
    } finally {
      fetchInProgressRef.current = false;
      if (mountedRef.current) {
        setLocalIsLoading(false);
      }
    }
  }, [isLoggedIn]);

  const consumeTokens = useCallback(
    async (
      feature: keyof typeof TOKEN_COSTS,
      description?: string,
    ): Promise<TokenConsumptionResult> => {
      const cost = TOKEN_COSTS[feature];
      const featureDescription = description ?? `${feature} usage`;

      setLocalIsLoading(true);
      setError(null);

      try {
        const result = await centralizedTokenService.consumeTokens(
          cost,
          isLoggedIn,
          featureDescription,
        );

        if (result.success) {
          // Update local state - the global event will also propagate to other components
          if (mountedRef.current) {
            setTokenInfo((prev) =>
              prev
                ? {
                    ...prev,
                    balance: result.newBalance,
                    lastUpdated: Date.now(),
                  }
                : null,
            );
          }
        } else {
          if (mountedRef.current) {
            setError(result.error ?? t`Token consumption failed`);
          }
        }

        return result;
      } catch (error_) {
        const errorMessage = error_ instanceof Error ? error_.message : "Token consumption failed";
        if (mountedRef.current) {
          setError(errorMessage);
        }
        return {
          success: false,
          newBalance: tokenBalance,
          error: errorMessage,
        };
      } finally {
        if (mountedRef.current) {
          setLocalIsLoading(false);
        }
      }
    },
    [isLoggedIn, tokenBalance],
  );

  const canAfford = useCallback(
    async (feature: keyof typeof TOKEN_COSTS): Promise<boolean> => {
      try {
        return await centralizedTokenService.canAfford(feature, isLoggedIn);
      } catch {
        return false;
      }
    },
    [isLoggedIn],
  );

  const getTokenCost = useCallback((feature: keyof typeof TOKEN_COSTS): number => {
    return centralizedTokenService.getTokenCost(feature);
  }, []);

  const formatTokens = useCallback((amount: number): string => {
    return amount.toLocaleString();
  }, []);

  // Listen for global token updates (unified event system)
  useEffect(() => {
    const handleTokenUpdate = (event: CustomEvent<{ balance: number }>) => {
      if (mountedRef.current) {
        setTokenInfo((prev) =>
          prev
            ? {
                ...prev,
                balance: event.detail.balance,
                lastUpdated: Date.now(),
              }
            : {
                balance: event.detail.balance,
                used: 0,
                limit: 0,
                percentage: 0,
                lastUpdated: Date.now(),
              },
        );
      }
    };

    window.addEventListener(TOKEN_BALANCE_EVENT, handleTokenUpdate as EventListener);

    return () => {
      window.removeEventListener(TOKEN_BALANCE_EVENT, handleTokenUpdate as EventListener);
    };
  }, []);

  // Initial fetch - use global store's fetchTokenBalance which has built-in deduplication
  useEffect(() => {
    // Only fetch if we haven't fetched yet (prevents React StrictMode double-invocation issues)
    if (globalTokenBalance === null && !fetchInProgressRef.current) {
      void fetchTokenBalance(isLoggedIn, false);
    }
  }, [fetchTokenBalance, isLoggedIn, globalTokenBalance]);

  // Cleanup
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    tokenBalance,
    tokenInfo,
    isLoading,
    error,
    refreshTokens,
    consumeTokens,
    canAfford,
    getTokenCost,
    formatTokens,
  };
};
